import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { setCookie } from "~/common/utils/cookie";
import { isForkPage, redirectToUrl } from "~/utils/url/urlUtils";
import { Button, IconButton, styled } from "~/common/mui";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { useDispatch, useSelector } from "react-redux";
import { updateVisibility } from "../redux/actions";
import { selectRegionaBannerVisibility } from "../redux/selectors";
import { useTranslation } from "react-i18next";
import GlobalBanner from "~/features/navigation/components/TopBannersSection/GlobalBanner/GlobalBanner";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { getRegionCookie } from "~/features/forkPage/utils/regionCookie";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";

interface Country {
  isoCode: string;
  name: string;
}

const dropdownOptions = window.SCHA.regionSelectionBanner.dropdownUrls;
const defaultDropdownOption = window.SCHA.regionSelectionBanner.dropdownUrls[0];

export const RegionSelectionBanner = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOpen = useSelector(selectRegionaBannerVisibility);
  const [dropdownOption, setDropdownOption] = useState(defaultDropdownOption);

  const isTabOrDesktop = useIsMinTabletSize();

  const handleConfirmClick = () => {
    dispatch(updateVisibility(false));
    if (dropdownOption.value == "1") {
      redirectToUrl(dropdownOption.url);
    } else {
      fetchRegionSiteAndRedirect(dropdownOption.url);
    }
  };

  const handleCloseClick = () => {
    dispatch(updateVisibility(false));
    setCookie({
      name: COOKIE,
      value: "closed",
      expireDate: dayjs().add(1, "w").toDate(), // expires in 1 week
    });
  };

  const options = useMemo(() => {
    return dropdownOptions.map((option) => {
      return {
        ...option,
        label: t(option.label),
      };
    });
  }, [t]);

  if (!isOpen) {
    return null;
  }

  return (
    <RegionSelectionBannerWrapper className="scha__screen-only">
      <RegionSelectionBannerRoot className="container">
        <RegionSelectionMessage>{t("regionSelectionBanner.message")}</RegionSelectionMessage>

        <RegionSelectionSelect>
          <SimpleSelect
            value={dropdownOption?.value}
            values={options}
            onChange={(e) => setDropdownOption(dropdownOptions[+e.target.value])}
            label={t("regionSelectionBanner.region")}
            id={"region_selection_dropdown"}
            fullWidth
          />
        </RegionSelectionSelect>
        <RegionSelectionActions>
          {isTabOrDesktop ? (
            <Button
              size={"medium"}
              className={"scha__region-selection-banner__confirm-button"}
              onClick={handleConfirmClick}
            >
              {t("regionSelectionBanner.apply")}
            </Button>
          ) : (
            <IconButton
              aria-label={t("regionSelectionBanner.apply")}
              variant="contained"
              color={"primary"}
              className={"scha__region-selection-banner__confirm-button"}
              onClick={handleConfirmClick}
            >
              <ArrowForwardIcon />
            </IconButton>
          )}

          <RegionSelectionCloseButton>
            <IconButton color={"primary"} onClick={handleCloseClick} aria-label={t("close")}>
              <CloseIcon  />
            </IconButton>
          </RegionSelectionCloseButton>
        </RegionSelectionActions>
      </RegionSelectionBannerRoot>
    </RegionSelectionBannerWrapper>
  );
};

const COOKIE = "cache.region";

const RegionSelectionBannerWrapper = styled("div")(({ theme }) => ({
  background: theme.palette.surface.main,
  zIndex: 200,
  [theme.breakpoints.up("tablet")]: {
    padding: `${theme.spacing(0.5)} 0`,
  },
}));

const RegionSelectionBannerRoot = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "auto auto",
  gridTemplateColumns: "1fr 40px",
  height: 165,
  alignItems: "center",
  [theme.breakpoints.up("tablet")]: {
    gridTemplateRows: "auto auto",
    gridTemplateColumns: "repeat(2, 1fr)",
    height: "auto",
  },
  [theme.breakpoints.up("desktop-s")]: {
    gridTemplateColumns: "2fr 1.3fr 208px",
    gridTemplateRows: "auto",
    height: "auto",
  },
}));

const RegionSelectionMessage = styled("div")(({ theme }) => ({
  ...theme.typography.bodyLarge,
  padding: `${theme.spacing(1.5)} 0`,
  gridColumn: "1 / 3",
  gridRow: "1",
  marginRight: theme.spacing(4),
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "1",
    gridRow: "1",
  },
}));

const RegionSelectionSelect = styled("div")(({ theme }) => ({
  gridColumn: "1",
  gridRow: "2",
  marginRight: theme.spacing(4),
  [theme.breakpoints.up("desktop-s")]: {
    gridColumn: "2",
    gridRow: "1",
  },
}));

const RegionSelectionActions = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "& .scha__region-selection-banner__confirm-button": {
    gridColumn: "2",
    gridRow: "2",
    width: "100%",
  },
});

const RegionSelectionCloseButton = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 10,
  right: 10,
  display: "flex",
  [theme.breakpoints.up("desktop-s")]: {
    position: "static",
    top: "auto",
    right: "auto",
    marginRight: theme.spacing(-1.25),
  },
}));
const paramsKey = "redirectedCountry";

export const showRegionBanner = () => {
  return window.SCHA.regionSelectionBanner.showRedirectBanner && getRegionCookie() !== "closed" && !isForkPage();
};

export function fetchRegionSiteAndRedirect(countryIsoCode: string) {
  let uri = window.location.href.replace(window.location.origin, "");
  if (isForkPage()) {
    uri = uri.replace("/static/welcome", "");
  }
  const params = new URLSearchParams();
  params.set("countryIsoCode", countryIsoCode);
  params.set("currentUri", uri);

  const url = resolveApiUrl(`/regionSelectionBanner/getRegionSite?${params}`);
  fetch(url, {
    method: "GET",
    credentials: "same-origin", // include, omit
    mode: "cors", // no-cors, same-origin
    referrer: "no-referrer", // client
    headers: {
      dataType: "json",
      "content-type": "application/json",
    },
  }).then((response) => {
    if (!response.ok) {
      throw alert("Error status: " + response.status + ", please try again");
    } else {
      response.json().then((data) => {
        const parsedData = JSON.parse(data);

        const url = new URL(parsedData.url);
        if (parsedData.isRedirectedCountry) {
          url.searchParams.set(paramsKey, countryIsoCode);
        }
        redirectToUrl(url.toString());
      });
    }
  });
}

export const RedirectionTopBanner = () => {
  const { t } = useTranslation();

  const [countryInfo, setCountryInfo] = useState<Country | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const country = urlParams.get(paramsKey);

    if (country && country.length > 0 && !isForkPage()) {
      const info = window.SCHA?.countryOptions?.find((item) => item.isoCode === country);
      setCountryInfo(info || null);
    }
  }, []);

  const handleCloseClick = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete(paramsKey);
    window.history.replaceState({}, "", url.toString());
    setCountryInfo(null);
  };

  const redirectionMessage = (
    <GlobalBanner type="secondary">
      <BannerContent className="container">
        <BannerMessage>
          {t("regionSelectionBanner.redirection.message")}
          {countryInfo && <strong>{` ${t(countryInfo.name)}.`}</strong>}
        </BannerMessage>
        <BannerAction>
          <IconButton color="primary" onClick={handleCloseClick}>
            <CloseIcon  />
          </IconButton>
        </BannerAction>
      </BannerContent>
    </GlobalBanner>
  );

  return (
    <div className="scha__root-region-banner">
      {countryInfo && redirectionMessage}
      {showRegionBanner() ? <RegionSelectionBanner /> : null}
    </div>
  );
};

const BannerContent = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

const BannerAction = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginRight: "-10px",

  [theme.breakpoints.down("tablet")]: {
    position: "absolute",
    top: "10px",
    right: "10px",
  },
}));

const BannerMessage = styled("div")(({ theme }) => ({
  ...getFontStyle(FontSize.TitleMedium),
  marginRight: "30px",

  [theme.breakpoints.down("tablet")]: {
    padding: "12px 0",
  },
}));
